import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useContent from '../hooks/useContent';
import ConfirmDialog from '../components/ConfirmDialog';

function StatusBadge({ status }) {
  return <span className={`badge ${status}`}>{status}</span>;
}

const TYPE_TABS = [
  { key: '', label: 'Alle' },
  { key: 'page', label: 'Seiten' },
  { key: 'post', label: 'Beiträge' },
];

export default function ContentList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ contentType: '', status: '' });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use REST API
  const contentApi = useContent();

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.contentType) params.type = filters.contentType;
      if (filters.status) params.status = filters.status;
      const result = await contentApi.listContent(params);
      setItems(result.items || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [contentApi, filters]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handlePublish = useCallback(async (item) => {
    try {
      await contentApi.updateContent(item.contentId, { status: 'published' });
      await loadContent();
    } catch (err) {
      console.error('Failed to publish:', err);
    }
  }, [contentApi, loadContent]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await contentApi.deleteContent(pendingDelete.contentId);
      setPendingDelete(null);
      await loadContent();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  }, [pendingDelete, contentApi, loadContent]);

  return (
    <div className="stack gap-lg">
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>Inhaltsverwaltung</h2>
            <p className="muted">Beiträge und Seiten verwalten.</p>
          </div>
          <div className="button-row">
            <button className="button secondary" onClick={() => navigate('/content/new?type=page')}>
              + Neue Seite
            </button>
            <button className="button primary" onClick={() => navigate('/content/new?type=post')}>
              + Neuer Beitrag
            </button>
          </div>
        </div>
        <div className="tabs">
          {TYPE_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`tab ${filters.contentType === key ? 'active' : ''}`}
              onClick={() => setFilters((current) => ({ ...current, contentType: key }))}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="filters-grid">
          <label>
            Status
            <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="deleted">Deleted</option>
            </select>
          </label>
        </div>
      </section>

      <section className="card">
        {loading ? <p>Lade Inhalte…</p> : null}
        {error ? <div className="alert error">{error.message}</div> : null}
        {!loading && items.length === 0 ? <p className="muted">Keine Inhalte gefunden.</p> : null}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Titel</th>
                <th>Typ</th>
                <th>Status</th>
                <th>Geändert</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.contentId}>
                  <td>
                    <strong>{item.title || <span className="muted">(Kein Titel)</span>}</strong>
                    <div className="muted small">/{item.slug}</div>
                  </td>
                  <td>{item.contentType === 'post' ? 'Beitrag' : 'Seite'}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td className="muted small">{item.updatedAt ? new Date(item.updatedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                  <td>
                    <div className="button-row compact end">
                      <Link className="button secondary small" to={`/content/${item.contentId}`}>Bearbeiten</Link>
                      {item.status !== 'published' ? (
                        <button className="button ghost small" onClick={() => handlePublish(item)}>
                          Veröffentlichen
                        </button>
                      ) : null}
                      <button className="button danger small" onClick={() => setPendingDelete(item)}>
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Inhalt löschen"
        message={`"${pendingDelete?.title || '(Kein Titel)'}" wirklich löschen?`}
        confirmLabel="Löschen"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
